import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Table,
  Modal,
  Form,
  Badge,
  Row,
  Col
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import {
  getAllSurveyShares,
  addSurveyShare,
  updateSurveyShare,
  deleteSurveyShare
} from 'services/surveyShares';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCopy,
  faEdit,
  faTrash,
  faEye
} from '@fortawesome/free-solid-svg-icons';

const SurveyShareManager = ({ surveyId }) => {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingShare, setEditingShare] = useState(null);
  const [formData, setFormData] = useState({
    person_name: '',
    is_valid: true
  });

  useEffect(() => {
    if (surveyId) {
      fetchShares();
    }
  }, [surveyId]);

  const fetchShares = async () => {
    setLoading(true);
    try {
      const response = await getAllSurveyShares(surveyId);
      if (response.result) {
        setShares(response.shares || []);
      } else {
        setShares([]);
      }
    } catch (error) {
      console.error('Error al obtener los shares:', error);
      setShares([]);
    }
    setLoading(false);
  };

  const handleShowModal = (share = null) => {
    if (share) {
      setEditingShare(share);
      setFormData({
        person_name: share.person_name,
        is_valid: share.is_valid
      });
    } else {
      setEditingShare(null);
      setFormData({
        person_name: '',
        is_valid: true
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingShare(null);
    setFormData({
      person_name: '',
      is_valid: true
    });
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!formData.person_name.trim()) {
      toast.error('El nombre de la persona es requerido', { theme: 'colored' });
      return;
    }

    try {
      let response;
      if (editingShare) {
        response = await updateSurveyShare(
          editingShare.id,
          formData.person_name,
          formData.is_valid
        );
      } else {
        response = await addSurveyShare(surveyId, formData.person_name);
      }

      if (response.result) {
        toast.success(
          editingShare
            ? 'Compartido actualizado correctamente'
            : 'Compartido creado correctamente',
          { theme: 'colored' }
        );
        handleCloseModal();
        fetchShares();
      } else {
        toast.error('Error al guardar el compartido', { theme: 'colored' });
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al procesar la solicitud', { theme: 'colored' });
    }
  };

  const handleDelete = async shareId => {
    if (
      window.confirm('¿Estás seguro de que deseas eliminar este compartido?')
    ) {
      try {
        const response = await deleteSurveyShare(shareId);
        if (response.result) {
          toast.success('Compartido eliminado correctamente', {
            theme: 'colored'
          });
          fetchShares();
        } else {
          toast.error('Error al eliminar el compartido', { theme: 'colored' });
        }
      } catch (error) {
        console.error('Error:', error);
        toast.error('Error al eliminar el compartido', { theme: 'colored' });
      }
    }
  };

  const copyToClipboard = token => {
    const url = `${window.location.origin}/shared-report/${token}`;
    navigator.clipboard
      .writeText(url)
      .then(() => {
        toast.success('URL copiada al portapapeles', { theme: 'colored' });
      })
      .catch(() => {
        toast.error('Error al copiar la URL', { theme: 'colored' });
      });
  };

  const openInNewTab = token => {
    const url = `${window.location.origin}/shared-report/${token}`;
    window.open(url, '_blank');
  };

  return (
    <Card className="mt-4">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <h6 className="mb-0">Gestión de Reportes Compartidos</h6>
        <Button size="sm" variant="primary" onClick={() => handleShowModal()}>
          Crear Nuevo Compartido
        </Button>
      </Card.Header>
      <Card.Body>
        {loading ? (
          <div className="text-center py-3">Cargando compartidos...</div>
        ) : shares.length === 0 ? (
          <div className="text-center py-3 text-muted">
            No hay reportes compartidos para esta encuesta
          </div>
        ) : (
          <Table striped bordered size="sm" responsive>
            <thead className="table-light">
              <tr>
                <th className="text-center">Persona</th>
                <th className="text-center">Estado</th>
                <th className="text-center">Visto</th>
                <th className="text-center">Creado</th>
                <th className="text-center">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {shares.map(share => (
                <tr key={share.id}>
                  <td className="text-center align-middle">
                    {share.person_name}
                  </td>
                  <td className="text-center align-middle">
                    <Badge bg={share.is_valid ? 'success' : 'secondary'}>
                      {share.is_valid ? 'Activo' : 'Inactivo'}
                    </Badge>
                  </td>
                  <td className="text-center align-middle">
                    {share.viewed_at ? (
                      <small>{share.viewed_at}</small>
                    ) : (
                      <Badge bg="warning" text="dark">
                        No visto
                      </Badge>
                    )}
                  </td>
                  <td className="text-center align-middle">
                    <small>{share.created_at}</small>
                  </td>
                  <td className="text-center align-middle">
                    <div className="d-flex justify-content-center gap-2">
                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() => copyToClipboard(share.token)}
                        title="Copiar URL"
                      >
                        <FontAwesomeIcon icon={faCopy} />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-info"
                        onClick={() => openInNewTab(share.token)}
                        title="Ver reporte"
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-warning"
                        onClick={() => handleShowModal(share)}
                        title="Editar"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => handleDelete(share.id)}
                        title="Eliminar"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>

      {/* Modal para crear/editar */}
      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingShare ? 'Editar Compartido' : 'Crear Nuevo Compartido'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Nombre de la Persona <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Ej: Juan Pérez"
                    value={formData.person_name}
                    onChange={e =>
                      setFormData({ ...formData, person_name: e.target.value })
                    }
                    size="sm"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            {editingShare && (
              <Row>
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      id="is_valid"
                      label="Compartido activo"
                      checked={formData.is_valid}
                      onChange={e =>
                        setFormData({ ...formData, is_valid: e.target.checked })
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal} size="sm">
              Cancelar
            </Button>
            <Button variant="primary" type="submit" size="sm">
              {editingShare ? 'Actualizar' : 'Crear'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Card>
  );
};

SurveyShareManager.propTypes = {
  surveyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  surveyName: PropTypes.string.isRequired
};

export default SurveyShareManager;
